import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {apiService} from '../services/api';
import {useAdmin} from './AdminContext';
import {formatDate, formatPrice} from './adminUtils';

interface AdminListingSummary {
  id: string;
  title: string;
  city?: string | null;
  price?: number | null;
  currency?: string | null;
  purpose?: string | null;
  status: string;
  ownerUserId: string;
  ownerEmail?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  moderationNote?: string | null;
  moderatedAt?: string | null;
}

interface AdminListingDetail {
  listing: any;
  ownerEmail?: string | null;
}

const AdminListingsPage: React.FC = () => {
  const {token} = useAdmin();
  const [listings, setListings] = useState<AdminListingSummary[]>([]);
  const [selectedListingId, setSelectedListingId] = useState<string | null>(null);
  const [selectedListing, setSelectedListing] = useState<AdminListingDetail | null>(null);
  const [loadingListings, setLoadingListings] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [dataError, setDataError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [purposeFilter, setPurposeFilter] = useState('');
  const [query, setQuery] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [moderationStatus, setModerationStatus] = useState('PUBLISHED');
  const [moderationNote, setModerationNote] = useState('');

  const loadListings = useCallback(async () => {
    if (!token) return;
    setLoadingListings(true);
    setDataError(null);
    try {
      const response = await apiService.adminGetListings(token, {
        status: statusFilter || undefined,
        purpose: purposeFilter || undefined,
        q: query.trim() || undefined,
        ownerId: ownerId.trim() || undefined,
      });
      setListings(response.items || []);
      if (response.items?.length) {
        setSelectedListingId((prev) => prev ?? response.items[0].id);
      } else {
        setSelectedListingId(null);
        setSelectedListing(null);
      }
    } catch (error) {
      setDataError(error instanceof Error ? error.message : 'Neuspesno ucitavanje nekretnina.');
    } finally {
      setLoadingListings(false);
    }
  }, [token, statusFilter, purposeFilter, query, ownerId]);

  useEffect(() => {
    loadListings();
  }, [loadListings]);

  const loadListingDetails = useCallback(async (listingId: string) => {
    if (!token) return;
    setLoadingDetails(true);
    setDataError(null);
    try {
      const details = await apiService.adminGetListingDetails(token, listingId);
      setSelectedListing(details);
      setModerationNote(details.listing?.moderation_note || '');
      setModerationStatus(details.listing?.status || 'PUBLISHED');
    } catch (error) {
      setSelectedListing(null);
      setDataError(error instanceof Error ? error.message : 'Neuspesno ucitavanje detalja.');
    } finally {
      setLoadingDetails(false);
    }
  }, [token]);

  useEffect(() => {
    if (!selectedListingId) return;
    loadListingDetails(selectedListingId);
  }, [loadListingDetails, selectedListingId]);

  const handleModerate = async () => {
    if (!token || !selectedListingId) return;
    setDataError(null);
    try {
      await apiService.adminModerateListing(token, selectedListingId, moderationStatus, moderationNote.trim() || undefined);
      await loadListings();
      await loadListingDetails(selectedListingId);
    } catch (error) {
      setDataError(error instanceof Error ? error.message : 'Neuspesno sacuvana moderacija.');
    }
  };

  const selectedSummary = useMemo(
    () => listings.find((listing) => listing.id === selectedListingId) || null,
    [listings, selectedListingId]
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-semibold text-neutral-900">Nekretnine</h2>
        <p className="mt-1 text-neutral-500">Moderacija i pregled svih oglasa.</p>
      </div>

      {dataError && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {dataError}
        </div>
      )}

      <div className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-soft">
        <div className="flex flex-wrap items-center gap-3">
          <input
            className="input-field max-w-sm"
            placeholder="Pretraga po naslovu, gradu ili adresi"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <input
            className="input-field max-w-xs"
            placeholder="Owner ID"
            value={ownerId}
            onChange={(event) => setOwnerId(event.target.value)}
          />
          <select
            className="input-field max-w-[160px]"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <option value="">Svi statusi</option>
            <option value="PUBLISHED">PUBLISHED</option>
            <option value="HIDDEN">HIDDEN</option>
            <option value="FLAGGED">FLAGGED</option>
          </select>
          <select
            className="input-field max-w-[160px]"
            value={purposeFilter}
            onChange={(event) => setPurposeFilter(event.target.value)}
          >
            <option value="">Svi tipovi</option>
            <option value="SALE">SALE</option>
            <option value="RENT">RENT</option>
          </select>
          <button className="btn-outline" type="button" onClick={loadListings} disabled={loadingListings}>
            {loadingListings ? 'Ucitavam...' : 'Primeni filtere'}
          </button>
        </div>
      </div>

      <div className="grid gap-8 lg:grid-cols-[420px_1fr]">
        <div className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-soft">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-xl font-semibold text-neutral-900">Lista oglasa</h3>
            <span className="text-sm text-neutral-500">{listings.length}</span>
          </div>
          {loadingListings ? (
            <div className="space-y-3">
              <div className="h-16 rounded-xl bg-neutral-100" />
              <div className="h-16 rounded-xl bg-neutral-100" />
              <div className="h-16 rounded-xl bg-neutral-100" />
            </div>
          ) : (
            <div className="space-y-3">
              {listings.map((listing) => (
                <button
                  key={listing.id}
                  type="button"
                  onClick={() => setSelectedListingId(listing.id)}
                  className={`w-full rounded-xl border px-4 py-3 text-left transition ${
                    listing.id === selectedListingId
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold text-neutral-900">{listing.title}</p>
                      <p className="text-sm text-neutral-500">{listing.city || '-'}</p>
                    </div>
                    <span className="text-xs font-semibold text-neutral-500">{listing.status}</span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-3 text-sm text-neutral-500">
                    <span>{formatPrice(listing.price, listing.currency)}</span>
                    <span>{listing.ownerEmail || '-'}</span>
                    <span>ID: {listing.ownerUserId}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="rounded-2xl border border-neutral-200 bg-white p-8 shadow-soft">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
              <div>
                <h3 className="text-xl font-semibold text-neutral-900">{selectedSummary?.title || 'Oglas'}</h3>
                <p className="text-sm text-neutral-500">{selectedSummary?.ownerEmail || '-'}</p>
                <p className="text-xs text-neutral-400">Owner ID: {selectedSummary?.ownerUserId || '-'}</p>
              </div>
              <div className="text-sm text-neutral-500">
                <span>Status: {selectedSummary?.status || '-'}</span>
              </div>
            </div>

            {loadingDetails ? (
              <div className="h-32 rounded-xl bg-neutral-100" />
            ) : !selectedListing ? (
              <p className="text-neutral-500">Izaberite oglas da vidite detalje.</p>
            ) : (
              <div className="space-y-4">
                <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-5">
                  <p className="text-sm text-neutral-500">Kontakt</p>
                  <p className="mt-2 text-sm text-neutral-900">{selectedListing.listing?.contact_name || '-'}</p>
                  <p className="text-xs text-neutral-500">{selectedListing.listing?.contact_email || '-'}</p>
                  <p className="text-xs text-neutral-500">{selectedListing.listing?.contact_phone || '-'}</p>
                </div>
                <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-5">
                  <p className="text-sm text-neutral-500">Detalji oglasa</p>
                  <div className="mt-2 grid gap-2 text-sm text-neutral-500">
                    <span>Grad: {selectedListing.listing?.city || '-'}</span>
                    <span>Adresa: {selectedListing.listing?.address || '-'}</span>
                    <span>Povrsina: {selectedListing.listing?.area_m2 || '-'}</span>
                    <span>Sobe: {selectedListing.listing?.rooms || '-'}</span>
                    <span>Datum: {formatDate(selectedListing.listing?.created_at)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-neutral-200 bg-white p-8 shadow-soft">
            <h4 className="text-xl font-semibold text-neutral-900">Moderacija</h4>
            <div className="mt-5 grid gap-3 md:grid-cols-2">
              <select
                className="input-field"
                value={moderationStatus}
                onChange={(event) => setModerationStatus(event.target.value)}
              >
                <option value="PUBLISHED">PUBLISHED</option>
                <option value="HIDDEN">HIDDEN</option>
                <option value="FLAGGED">FLAGGED</option>
              </select>
              <input
                className="input-field"
                placeholder="Interna beleska"
                value={moderationNote}
                onChange={(event) => setModerationNote(event.target.value)}
              />
            </div>
            <button className="btn-outline mt-4" type="button" onClick={handleModerate}>
              Sacuvaj moderaciju
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminListingsPage;
